// src/pages/admin/AdminTicket.tsx

import React, {
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";

import "./AdminTicket.css";


// =====================================================
// CONFIGURATION
// =====================================================

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000";


// =====================================================
// TYPES
// =====================================================

interface Ticket {
  id: string;

  ticket_number: string;

  user_id:
    | string
    | null;

  ticket_type:
    | "contact"
    | "problem";

  status:
    | "new"
    | "in_progress"
    | "waiting_for_user"
    | "resolved"
    | "closed";

  priority:
    | "low"
    | "normal"
    | "high"
    | "urgent";

  subject: string;

  requester_name:
    | string
    | null;

  requester_email:
    | string
    | null;

  assigned_admin_user_id:
    | string
    | null;

  created_at: string;

  updated_at: string;

  resolved_at:
    | string
    | null;

  closed_at:
    | string
    | null;

  requester_username:
    | string
    | null;

  registered_user_name:
    | string
    | null;

  registered_user_email:
    | string
    | null;

  assigned_admin_username:
    | string
    | null;

  assigned_admin_name:
    | string
    | null;
}


interface ContactMessage {
  id: string;

  category: string;

  message: string;

  created_at: string;
}


interface ProblemReport {
  id: string;

  problem_type: string;

  game_name:
    | string
    | null;

  device: string;

  description: string;

  steps_to_reproduce:
    | string
    | null;

  room_id:
    | string
    | null;

  match_id:
    | string
    | null;

  created_at: string;
}


interface SupportReply {
  id: string;

  ticket_id: string;

  sender_user_id:
    | string
    | null;

  sender_type:
    | "user"
    | "admin"
    | "system";

  message: string;

  created_at: string;

  sender_username?:
    | string
    | null;

  sender_name?:
    | string
    | null;
}


interface TicketResponse {
  success: boolean;

  ticket?: Ticket;

  contactMessage?:
    | ContactMessage
    | null;

  problemReport?:
    | ProblemReport
    | null;

  replies?: SupportReply[];

  message?: string;
}


interface ReplyResponse {
  success: boolean;

  message?: string;

  reply?: SupportReply;
}


// =====================================================
// DATE FORMATTER
// =====================================================

const formatDate = (
  value:
    | string
    | null
    | undefined
) => {

  if (!value) {
    return "—";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value;
  }

  return date.toLocaleString(
    undefined,
    {
      year: "numeric",
      month: "short",
      day: "numeric",

      hour: "numeric",
      minute: "2-digit",
    }
  );
};


// =====================================================
// TEXT FORMATTER
// =====================================================

const formatLabel = (
  value:
    | string
    | null
    | undefined
) => {

  if (!value) {
    return "—";
  }

  return value
    .replace(/_/g, " ")
    .replace(
      /\b\w/g,
      (character) =>
        character.toUpperCase()
    );
};


// =====================================================
// ADMIN TICKET
// =====================================================

const AdminTicket:
React.FC = () => {

  const {
    ticketId,
  } = useParams<{
    ticketId: string;
  }>();


  const navigate =
    useNavigate();


  // ===================================================
  // STATE
  // ===================================================

  const [
    ticket,
    setTicket,
  ] =
    useState<Ticket | null>(
      null
    );


  const [
    contactMessage,
    setContactMessage,
  ] =
    useState<ContactMessage | null>(
      null
    );


  const [
    problemReport,
    setProblemReport,
  ] =
    useState<ProblemReport | null>(
      null
    );


  const [
    replies,
    setReplies,
  ] =
    useState<SupportReply[]>(
      []
    );


  const [
    replyMessage,
    setReplyMessage,
  ] =
    useState("");


  const [
    loading,
    setLoading,
  ] =
    useState(true);


  const [
    sendingReply,
    setSendingReply,
  ] =
    useState(false);


  const [
    error,
    setError,
  ] =
    useState("");


  const [
    replyNotice,
    setReplyNotice,
  ] =
    useState("");


  // ===================================================
  // TOKEN
  // ===================================================

  const getToken = () => {
    return localStorage.getItem(
      "token"
    );
  };


  // ===================================================
  // LOAD TICKET
  // ===================================================

  const loadTicket =
    useCallback(
      async () => {

        if (!ticketId) {

          setError(
            "Ticket ID is missing."
          );

          setLoading(false);

          return;
        }


        const token =
          getToken();


        if (!token) {

          setError(
            "Administrator authentication is required."
          );

          setLoading(false);

          return;
        }


        try {

          setLoading(true);

          setError("");


          const response =
            await fetch(
              `${API_BASE_URL}/api/admin/support/tickets/${ticketId}`,
              {
                method: "GET",

                headers: {
                  Authorization:
                    `Bearer ${token}`,
                },
              }
            );


          const data:
            TicketResponse =
              await response.json();


          if (
            response.status === 401
          ) {

            localStorage.removeItem(
              "token"
            );

            navigate(
              "/login",
              {
                replace: true,
              }
            );

            return;
          }


          if (
            response.status === 403
          ) {

            setError(
              data.message ||
              "You do not have administrator access."
            );

            return;
          }


          if (
            !response.ok ||
            !data.success ||
            !data.ticket
          ) {

            throw new Error(
              data.message ||
              "Unable to load support ticket."
            );
          }


          setTicket(
            data.ticket
          );


          setContactMessage(
            data.contactMessage ||
            null
          );


          setProblemReport(
            data.problemReport ||
            null
          );


          setReplies(
            data.replies ||
            []
          );

        } catch (err) {

          console.error(
            "Unable to load admin support ticket:",
            err
          );


          setError(
            err instanceof Error
              ? err.message
              : "Unable to load support ticket."
          );

        } finally {

          setLoading(false);

        }

      },
      [
        ticketId,
        navigate,
      ]
    );


  // ===================================================
  // INITIAL LOAD
  // ===================================================

  useEffect(
    () => {

      loadTicket();

    },
    [
      loadTicket,
    ]
  );


  // ===================================================
  // SEND REPLY
  // ===================================================

  const handleReplySubmit =
    async (
      event:
        FormEvent<HTMLFormElement>
    ) => {

      event.preventDefault();


      if (!ticketId) {
        return;
      }


      const cleanMessage =
        replyMessage.trim();


      if (!cleanMessage) {

        setReplyNotice(
          "Please enter a reply."
        );

        return;
      }


      if (
        cleanMessage.length >
        5000
      ) {

        setReplyNotice(
          "Reply cannot exceed 5,000 characters."
        );

        return;
      }


      const token =
        getToken();


      if (!token) {

        navigate(
          "/login",
          {
            replace: true,
          }
        );

        return;
      }


      try {

        setSendingReply(true);

        setReplyNotice("");


        const response =
          await fetch(
            `${API_BASE_URL}/api/admin/support/tickets/${ticketId}/replies`,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",

                Authorization:
                  `Bearer ${token}`,
              },

              body:
                JSON.stringify({
                  message:
                    cleanMessage,
                }),
            }
          );


        const data:
          ReplyResponse =
            await response.json();


        if (
          response.status === 401
        ) {

          localStorage.removeItem(
            "token"
          );

          navigate(
            "/login",
            {
              replace: true,
            }
          );

          return;
        }


        if (
          response.status === 403
        ) {

          setReplyNotice(
            data.message ||
            "Administrator access is required."
          );

          return;
        }


        if (
          !response.ok ||
          !data.success
        ) {

          throw new Error(
            data.message ||
            "Unable to send reply."
          );
        }


        setReplyMessage("");


        setReplyNotice(
          "Reply sent successfully."
        );


        /*
          Reload ticket so reply history
          always matches the database.
        */

        await loadTicket();

      } catch (err) {

        console.error(
          "Unable to send support reply:",
          err
        );


        setReplyNotice(
          err instanceof Error
            ? err.message
            : "Unable to send reply."
        );

      } finally {

        setSendingReply(false);

      }
    };


  // ===================================================
  // LOADING
  // ===================================================

  if (loading) {

    return (
      <main className="admin-ticket-page">

        <div className="admin-ticket-loading">

          <div
            className="admin-ticket-spinner"
          />

          <p>
            Loading support ticket...
          </p>

        </div>

      </main>
    );
  }


  // ===================================================
  // ERROR
  // ===================================================

  if (
    error ||
    !ticket
  ) {

    return (
      <main className="admin-ticket-page">

        <div className="admin-ticket-error-page">

          <div
            className="admin-ticket-error-card"
          >

            <span
              className="admin-ticket-error-icon"
            >
              !
            </span>

            <h1>
              Unable to Open Ticket
            </h1>

            <p>
              {
                error ||
                "The requested support ticket could not be found."
              }
            </p>

            <div
              className="admin-ticket-error-actions"
            >

              <button
                type="button"
                onClick={
                  loadTicket
                }
              >
                Try Again
              </button>

              <Link
                to="/admin/support"
              >
                Back to Support
              </Link>

            </div>

          </div>

        </div>

      </main>
    );
  }


  // ===================================================
  // REQUESTER INFORMATION
  // ===================================================

  const requesterName =
    ticket.registered_user_name ||
    ticket.requester_name ||
    ticket.requester_username ||
    "Guest Player";


  const requesterEmail =
    ticket.registered_user_email ||
    ticket.requester_email ||
    "Not provided";


  // ===================================================
  // UI
  // ===================================================

  return (
    <main className="admin-ticket-page">

      {/* ============================================= */}
      {/* HEADER                                        */}
      {/* ============================================= */}

      <header
        className="admin-ticket-header"
      >

        <div
          className="admin-ticket-header-inner"
        >

          <div
            className="admin-ticket-brand"
          >

            <Link
              to="/"
              className="admin-ticket-logo"
            >
              P-ARENA
            </Link>

            <span
              className="admin-ticket-divider"
            >
              /
            </span>

            <Link
              to="/admin/support"
              className="admin-ticket-section-link"
            >
              Support
            </Link>

          </div>


          <Link
            to="/admin/support"
            className="admin-ticket-back"
          >
            ← Support Inbox
          </Link>

        </div>

      </header>


      {/* ============================================= */}
      {/* HERO                                          */}
      {/* ============================================= */}

      <section
        className="admin-ticket-hero"
      >

        <div
          className="admin-ticket-hero-glow"
        />


        <div
          className="admin-ticket-container admin-ticket-hero-inner"
        >

          <div>

            <p
              className="admin-ticket-eyebrow"
            >
              SUPPORT TICKET
            </p>


            <div
              className="admin-ticket-title-row"
            >

              <h1>
                {
                  ticket.ticket_number
                }
              </h1>


              <span
                className={
                  `admin-ticket-status admin-ticket-status-${ticket.status}`
                }
              >
                {
                  formatLabel(
                    ticket.status
                  )
                }
              </span>

            </div>


            <p
              className="admin-ticket-hero-subject"
            >
              {
                ticket.subject
              }
            </p>

          </div>


          <div
            className="admin-ticket-hero-meta"
          >

            <div>

              <span>
                Type
              </span>

              <strong>
                {
                  formatLabel(
                    ticket.ticket_type
                  )
                }
              </strong>

            </div>


            <div>

              <span>
                Priority
              </span>

              <strong
                className={
                  `admin-ticket-priority-text admin-ticket-priority-text-${ticket.priority}`
                }
              >
                {
                  formatLabel(
                    ticket.priority
                  )
                }
              </strong>

            </div>


            <div>

              <span>
                Created
              </span>

              <strong>
                {
                  formatDate(
                    ticket.created_at
                  )
                }
              </strong>

            </div>

          </div>

        </div>

      </section>


      {/* ============================================= */}
      {/* MAIN CONTENT                                  */}
      {/* ============================================= */}

      <div
        className="admin-ticket-container admin-ticket-main"
      >

        <div
          className="admin-ticket-layout"
        >

          {/* ========================================= */}
          {/* LEFT COLUMN                               */}
          {/* ========================================= */}

          <div
            className="admin-ticket-content-column"
          >

            {/* ======================================= */}
            {/* REQUEST                                */}
            {/* ======================================= */}

            <section
              className="admin-ticket-card"
            >

              <div
                className="admin-ticket-card-heading"
              >

                <div>

                  <p>
                    ORIGINAL REQUEST
                  </p>

                  <h2>
                    {
                      ticket.ticket_type ===
                      "problem"
                        ? "Problem Report"
                        : "Contact Message"
                    }
                  </h2>

                </div>


                <span
                  className={
                    `admin-ticket-type admin-ticket-type-${ticket.ticket_type}`
                  }
                >
                  {
                    formatLabel(
                      ticket.ticket_type
                    )
                  }
                </span>

              </div>


              {/* ===================================== */}
              {/* CONTACT MESSAGE                       */}
              {/* ===================================== */}

              {
                ticket.ticket_type ===
                  "contact" &&
                contactMessage && (

                  <div
                    className="admin-ticket-request-body"
                  >

                    <div
                      className="admin-ticket-detail-grid"
                    >

                      <div>

                        <span>
                          Category
                        </span>

                        <strong>
                          {
                            formatLabel(
                              contactMessage.category
                            )
                          }
                        </strong>

                      </div>


                      <div>

                        <span>
                          Submitted
                        </span>

                        <strong>
                          {
                            formatDate(
                              contactMessage.created_at
                            )
                          }
                        </strong>

                      </div>

                    </div>


                    <div
                      className="admin-ticket-message-block"
                    >

                      <span>
                        Message
                      </span>

                      <p>
                        {
                          contactMessage.message
                        }
                      </p>

                    </div>

                  </div>

                )
              }


              {/* ===================================== */}
              {/* PROBLEM REPORT                        */}
              {/* ===================================== */}

              {
                ticket.ticket_type ===
                  "problem" &&
                problemReport && (

                  <div
                    className="admin-ticket-request-body"
                  >

                    <div
                      className="admin-ticket-detail-grid admin-ticket-detail-grid-problem"
                    >

                      <div>

                        <span>
                          Problem Type
                        </span>

                        <strong>
                          {
                            formatLabel(
                              problemReport.problem_type
                            )
                          }
                        </strong>

                      </div>


                      <div>

                        <span>
                          Game
                        </span>

                        <strong>
                          {
                            problemReport.game_name ||
                            "—"
                          }
                        </strong>

                      </div>


                      <div>

                        <span>
                          Device
                        </span>

                        <strong>
                          {
                            problemReport.device
                          }
                        </strong>

                      </div>


                      <div>

                        <span>
                          Submitted
                        </span>

                        <strong>
                          {
                            formatDate(
                              problemReport.created_at
                            )
                          }
                        </strong>

                      </div>


                      <div>

                        <span>
                          Room ID
                        </span>

                        <strong>
                          {
                            problemReport.room_id ||
                            "—"
                          }
                        </strong>

                      </div>


                      <div>

                        <span>
                          Match ID
                        </span>

                        <strong>
                          {
                            problemReport.match_id ||
                            "—"
                          }
                        </strong>

                      </div>

                    </div>


                    <div
                      className="admin-ticket-message-block"
                    >

                      <span>
                        Description
                      </span>

                      <p>
                        {
                          problemReport.description
                        }
                      </p>

                    </div>


                    {
                      problemReport.steps_to_reproduce && (

                        <div
                          className="admin-ticket-message-block"
                        >

                          <span>
                            Steps to Reproduce
                          </span>

                          <p>
                            {
                              problemReport.steps_to_reproduce
                            }
                          </p>

                        </div>

                      )
                    }

                  </div>

                )
              }

            </section>


            {/* ======================================= */}
            {/* CONVERSATION                            */}
            {/* ======================================= */}

            <section
              className="admin-ticket-card admin-ticket-conversation-card"
            >

              <div
                className="admin-ticket-card-heading"
              >

                <div>

                  <p>
                    COMMUNICATION
                  </p>

                  <h2>
                    Conversation
                  </h2>

                </div>


                <span
                  className="admin-ticket-reply-count"
                >
                  {
                    replies.length
                  }
                  {
                    replies.length === 1
                      ? " Reply"
                      : " Replies"
                  }
                </span>

              </div>


              <div
                className="admin-ticket-conversation"
              >

                {
                  replies.length === 0 ? (

                    <div
                      className="admin-ticket-no-replies"
                    >

                      <span>
                        ◇
                      </span>

                      <h3>
                        No Replies Yet
                      </h3>

                      <p>
                        This ticket has not received
                        a response yet.
                      </p>

                    </div>

                  ) : (

                    replies.map(
                      (
                        reply
                      ) => (

                        <article
                          key={
                            reply.id
                          }
                          className={
                            `admin-ticket-reply admin-ticket-reply-${reply.sender_type}`
                          }
                        >

                          <div
                            className="admin-ticket-reply-header"
                          >

                            <div
                              className="admin-ticket-reply-author"
                            >

                              <span
                                className="admin-ticket-reply-avatar"
                              >
                                {
                                  reply.sender_type ===
                                  "admin"
                                    ? "A"
                                    : reply.sender_type ===
                                      "system"
                                      ? "S"
                                      : "U"
                                }
                              </span>


                              <div>

                                <strong>
                                  {
                                    reply.sender_type ===
                                    "admin"
                                      ? (
                                        reply.sender_name ||
                                        reply.sender_username ||
                                        "P-ARENA Support"
                                      )
                                      : reply.sender_type ===
                                        "system"
                                        ? "P-ARENA System"
                                        : (
                                          reply.sender_name ||
                                          reply.sender_username ||
                                          requesterName
                                        )
                                  }
                                </strong>

                                <span>
                                  {
                                    reply.sender_type ===
                                    "admin"
                                      ? "Administrator"
                                      : formatLabel(
                                          reply.sender_type
                                        )
                                  }
                                </span>

                              </div>

                            </div>


                            <time>
                              {
                                formatDate(
                                  reply.created_at
                                )
                              }
                            </time>

                          </div>


                          <p
                            className="admin-ticket-reply-message"
                          >
                            {
                              reply.message
                            }
                          </p>

                        </article>

                      )
                    )

                  )
                }

              </div>

            </section>


            {/* ======================================= */}
            {/* REPLY                                   */}
            {/* ======================================= */}

            <section
              className="admin-ticket-card admin-ticket-reply-card"
            >

              <div
                className="admin-ticket-card-heading"
              >

                <div>

                  <p>
                    ADMIN RESPONSE
                  </p>

                  <h2>
                    Reply to Player
                  </h2>

                </div>

              </div>


              <form
                className="admin-ticket-reply-form"
                onSubmit={
                  handleReplySubmit
                }
              >

                <label
                  htmlFor="admin-ticket-reply"
                >
                  Message
                </label>


                <textarea
                  id="admin-ticket-reply"
                  value={
                    replyMessage
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setReplyMessage(
                        event.target.value
                      )
                  }
                  rows={7}
                  maxLength={5000}
                  placeholder="Write your response to the player..."
                  disabled={
                    sendingReply
                  }
                  required
                />


                <div
                  className="admin-ticket-reply-footer"
                >

                  <span>
                    {
                      replyMessage.length
                    }
                    {" / 5000"}
                  </span>


                  <button
                    type="submit"
                    disabled={
                      sendingReply ||
                      !replyMessage.trim()
                    }
                  >
                    {
                      sendingReply
                        ? "Sending..."
                        : "Send Reply"
                    }
                  </button>

                </div>


                {
                  replyNotice && (

                    <div
                      className="admin-ticket-reply-notice"
                      role="status"
                    >
                      {
                        replyNotice
                      }
                    </div>

                  )
                }

              </form>

            </section>

          </div>


          {/* ========================================= */}
          {/* RIGHT SIDEBAR                             */}
          {/* ========================================= */}

          <aside
            className="admin-ticket-sidebar"
          >

            {/* ======================================= */}
            {/* REQUESTER                               */}
            {/* ======================================= */}

            <section
              className="admin-ticket-side-card"
            >

              <p
                className="admin-ticket-side-label"
              >
                REQUESTER
              </p>


              <div
                className="admin-ticket-requester-profile"
              >

                <div
                  className="admin-ticket-requester-avatar"
                >
                  {
                    requesterName
                      .charAt(0)
                      .toUpperCase()
                  }
                </div>


                <div>

                  <strong>
                    {
                      requesterName
                    }
                  </strong>

                  {
                    ticket.requester_username && (

                      <span>
                        @
                        {
                          ticket.requester_username
                        }
                      </span>

                    )
                  }

                </div>

              </div>


              <div
                className="admin-ticket-side-details"
              >

                <div>

                  <span>
                    Email
                  </span>

                  <strong>
                    {
                      requesterEmail
                    }
                  </strong>

                </div>


                <div>

                  <span>
                    Account
                  </span>

                  <strong>
                    {
                      ticket.user_id
                        ? "Registered Player"
                        : "Guest Submission"
                    }
                  </strong>

                </div>


                {
                  ticket.user_id && (

                    <div>

                      <span>
                        User ID
                      </span>

                      <strong>
                        {
                          ticket.user_id
                        }
                      </strong>

                    </div>

                  )
                }

              </div>

            </section>


            {/* ======================================= */}
            {/* TICKET DETAILS                          */}
            {/* ======================================= */}

            <section
              className="admin-ticket-side-card"
            >

              <p
                className="admin-ticket-side-label"
              >
                TICKET DETAILS
              </p>


              <div
                className="admin-ticket-side-details"
              >

                <div>

                  <span>
                    Ticket
                  </span>

                  <strong>
                    {
                      ticket.ticket_number
                    }
                  </strong>

                </div>


                <div>

                  <span>
                    Type
                  </span>

                  <strong>
                    {
                      formatLabel(
                        ticket.ticket_type
                      )
                    }
                  </strong>

                </div>


                <div>

                  <span>
                    Status
                  </span>

                  <strong>
                    {
                      formatLabel(
                        ticket.status
                      )
                    }
                  </strong>

                </div>


                <div>

                  <span>
                    Priority
                  </span>

                  <strong>
                    {
                      formatLabel(
                        ticket.priority
                      )
                    }
                  </strong>

                </div>


                <div>

                  <span>
                    Created
                  </span>

                  <strong>
                    {
                      formatDate(
                        ticket.created_at
                      )
                    }
                  </strong>

                </div>


                <div>

                  <span>
                    Updated
                  </span>

                  <strong>
                    {
                      formatDate(
                        ticket.updated_at
                      )
                    }
                  </strong>

                </div>

              </div>

            </section>


            {/* ======================================= */}
            {/* ASSIGNMENT                              */}
            {/* ======================================= */}

            <section
              className="admin-ticket-side-card"
            >

              <p
                className="admin-ticket-side-label"
              >
                ASSIGNMENT
              </p>


              {
                ticket.assigned_admin_user_id ? (

                  <div
                    className="admin-ticket-assigned-admin"
                  >

                    <span>
                      A
                    </span>


                    <div>

                      <strong>
                        {
                          ticket.assigned_admin_name ||
                          ticket.assigned_admin_username ||
                          "Administrator"
                        }
                      </strong>

                      {
                        ticket.assigned_admin_username && (

                          <small>
                            @
                            {
                              ticket.assigned_admin_username
                            }
                          </small>

                        )
                      }

                    </div>

                  </div>

                ) : (

                  <div
                    className="admin-ticket-unassigned"
                  >

                    <strong>
                      Unassigned
                    </strong>

                    <p>
                      No administrator has been
                      assigned to this ticket yet.
                    </p>

                  </div>

                )
              }

            </section>


            {/* ======================================= */}
            {/* LIFECYCLE                               */}
            {/* ======================================= */}

            {
              (
                ticket.resolved_at ||
                ticket.closed_at
              ) && (

                <section
                  className="admin-ticket-side-card"
                >

                  <p
                    className="admin-ticket-side-label"
                  >
                    LIFECYCLE
                  </p>


                  <div
                    className="admin-ticket-side-details"
                  >

                    {
                      ticket.resolved_at && (

                        <div>

                          <span>
                            Resolved
                          </span>

                          <strong>
                            {
                              formatDate(
                                ticket.resolved_at
                              )
                            }
                          </strong>

                        </div>

                      )
                    }


                    {
                      ticket.closed_at && (

                        <div>

                          <span>
                            Closed
                          </span>

                          <strong>
                            {
                              formatDate(
                                ticket.closed_at
                              )
                            }
                          </strong>

                        </div>

                      )
                    }

                  </div>

                </section>

              )
            }

          </aside>

        </div>

      </div>

    </main>
  );
};


// =====================================================
// EXPORT
// =====================================================

export default AdminTicket;